/**
 * Local player controller.
 *
 * Owns input sampling, the fixed-step accumulator, and render interpolation. All movement-
 * DETERMINING logic (gravity, jump buffer/coyote, direction smoothing, collision, the
 * unknown-chunk freeze rule) lives in the shared voxel physics engine so it is deterministic
 * and replayable — see shared/src/physics. This class deliberately keeps only presentation
 * concerns on the client side.
 *
 * `position` is the EYE position and is the TICK state: streaming, persistence and the
 * network input all read it. The camera instead reads `getRenderEyePosition()`, which
 * interpolates between the last two ticks so motion stays smooth at any frame rate.
 */

import * as THREE from 'three';
import {
  MovementInput,
  PlayerSnapshot,
  INPUT_JUMP,
  INPUT_SPRINT,
  MAX_FALL_TIME,
  PLAYER_HEIGHT_INNER,
  PLAYER_RADIUS,
  PHYSICS_FIXED_DT,
  PHYSICS_TICK_RATE,
  FixedStepDriver,
  InputLatch,
  VoxelField,
  createPlayerState,
  stepPlayer,
  teleportPlayer,
  getMovementFromButtons,
  type PlayerInputCmd,
  type PlayerState,
  type PlayerStepResult,
  type ColumnHit,
  type VoxelPhysicsWorld,
} from '@worldify/shared';
import { Controls } from './controls';

/** Ticks of continuous falling before the "have I fallen out of the world?" check runs. */
const MAX_FALL_TICKS = MAX_FALL_TIME * PHYSICS_TICK_RATE;

/**
 * How far below the feet to look for anything to land on (m). Past the tallest relief the terrain
 * sliders can produce (4 km), so a dive down the full height of the world still finds its floor.
 */
const WORLD_FLOOR_REACH = 5000;

const _fallProbe: ColumnHit = { found: false, y: 0, startsSolid: false };

/** Callback type for respawn requests */
export type RespawnCallback = (currentPos: THREE.Vector3, lastGroundedPos: THREE.Vector3 | null) => void;

export class PlayerLocal {
  /** EYE position at the last completed physics tick (authoritative game state). */
  public position = new THREE.Vector3(0, 0, 0);
  public yaw = 0;
  public pitch = 0;

  /** Velocity at the last completed physics tick. */
  public velocity = new THREE.Vector3(0, 0, 0);

  // Physics engine state
  private readonly sim: PlayerState = createPlayerState(0, 0, 0);
  private readonly stepResult: PlayerStepResult = {
    grounded: false, frozen: false, blockedByUnknown: false, stuck: false,
    steppedUp: false, hitCeiling: false, wallContact: false, jumped: false, depenetrations: 0,
  };
  private readonly driver = new FixedStepDriver();
  private field: VoxelField | null = null;
  private readonly input: PlayerInputCmd = { moveX: 0, moveZ: 0, yaw: 0, sprint: false, jumpHeld: false, jumpPressed: false };

  /**
   * Jump edge carried between frames. Cleared ONLY by a tick that actually simulated, so a
   * frame running zero ticks (common above 60 fps) cannot swallow the press.
   */
  private readonly jumpLatch = new InputLatch();

  // Render interpolation: the eye position at the previous and current tick
  private readonly prevEye = new THREE.Vector3();
  private readonly renderEye = new THREE.Vector3();

  private inputSeq = 0;
  private fallTicks = 0;
  private lastGroundedPosition: THREE.Vector3 | null = null;
  private onRespawnNeeded: RespawnCallback | null = null;

  /**
   * Point physics at a voxel world. Must be called before physics can run — until it is,
   * update() is a no-op (there is nothing to collide against, and guessing would mean
   * falling through a world we simply cannot see yet).
   */
  setWorldSource(source: VoxelPhysicsWorld): VoxelField {
    this.field = new VoxelField(source);
    this.driver.reset();
    return this.field;
  }

  /** The physics field, for callers that must invalidate chunks on ingest/unload. */
  getField(): VoxelField | null {
    return this.field;
  }

  /**
   * Set callback for when player needs to respawn (fell too long)
   */
  setRespawnCallback(callback: RespawnCallback): void {
    this.onRespawnNeeded = callback;
  }

  /**
   * Get the last position where the player was grounded
   */
  getLastGroundedPosition(): THREE.Vector3 | null {
    return this.lastGroundedPosition;
  }

  /**
   * Get current input state to send to server
   */
  getInput(controls: Controls): MovementInput {
    return {
      buttons: controls.getButtonMask(),
      yaw: controls.yaw,
      pitch: controls.pitch,
      seq: this.inputSeq++,
      x: this.position.x,
      y: this.position.y,
      z: this.position.z,
    };
  }

  /**
   * Apply authoritative state from server snapshot
   * Movement is client-authoritative, so we ignore server position
   */
  applyServerState(_snapshot: PlayerSnapshot): void {
    // Client-authoritative movement: don't apply server position
    // Server just relays our position to other clients
  }

  /**
   * Advance physics by a render frame's worth of time.
   *
   * The accumulator runs whole fixed ticks; leftover sub-tick time becomes the render
   * interpolation factor. Look direction is read straight from controls every frame — mouse
   * look must never wait on a tick.
   */
  update(deltaMs: number, controls: Controls): void {
    this.yaw = controls.yaw;
    this.pitch = controls.pitch;

    // No world yet: hold position rather than integrate blind.
    if (!this.field) return;

    const buttons = controls.getButtonMask();
    const analog = controls.getMoveVector();
    // Keyboard reports a unit direction; touch reports an analog vector whose LENGTH is the
    // throttle. Fall back to the button mask when the analog stick is idle.
    let moveX = analog.moveX;
    let moveZ = analog.moveZ;
    if (moveX === 0 && moveZ === 0) {
      const fromButtons = getMovementFromButtons(buttons);
      moveX = fromButtons.moveX;
      moveZ = fromButtons.moveZ;
    }
    this.input.moveX = moveX;
    this.input.moveZ = moveZ;
    this.input.yaw = controls.yaw;
    this.input.sprint = (buttons & INPUT_SPRINT) !== 0;
    this.input.jumpHeld = (buttons & INPUT_JUMP) !== 0;
    if (controls.consumeJumpPress()) this.jumpLatch.press();
    this.input.jumpPressed = this.jumpLatch.armed;

    const ticks = this.driver.feed(deltaMs, this.tickOnce);
    if (ticks > 0) {
      this.position.set(this.sim.x, this.sim.y, this.sim.z);
      this.velocity.set(this.sim.vx, this.sim.vy, this.sim.vz);
    }

    // Always interpolate. `prevEye` is now the state at the start of the LAST tick (set in
    // tickOnce), so tick-less frames keep advancing the camera smoothly toward the current
    // tick instead of pinning it — the old per-frame capture produced a 0, 0, 0.25, 0.75
    // stutter at 144 Hz, i.e. more judder than no interpolation at all.
    this.renderEye.copy(this.prevEye).lerp(this.position, this.driver.alpha);
  }

  /** One fixed physics tick (bound so the driver can call it without allocating). */
  private tickOnce = (): void => {
    // Capture the pre-tick pose HERE, not once per frame: with 2+ ticks in a frame a
    // per-frame capture spans several ticks while alpha only covers one.
    this.prevEye.set(this.sim.x, this.sim.y, this.sim.z);

    stepPlayer(this.sim, this.input, this.field!, PHYSICS_FIXED_DT, this.stepResult);

    // A frozen tick never ran the sim, so it must not eat the queued jump.
    this.jumpLatch.settle(!this.stepResult.frozen);
    this.input.jumpPressed = this.jumpLatch.armed;

    if (this.stepResult.grounded) {
      if (!this.lastGroundedPosition) this.lastGroundedPosition = new THREE.Vector3();
      this.lastGroundedPosition.set(this.sim.x, this.sim.y, this.sim.z);
    }

    // Frozen ticks are NOT falling: the player is suspended waiting for chunk data, and
    // counting that time is what used to respawn people for streaming slowly.
    if (this.stepResult.frozen) return;

    if (!this.stepResult.grounded && this.sim.vy < 0) {
      this.fallTicks++;
    } else {
      this.fallTicks = 0;
    }

    // A wedged player can't be saved by more ticks; respawn on the same path as a long fall.
    if (this.stepResult.stuck) {
      this.triggerRespawn();
      return;
    }

    // A LONG FALL IS NOT THE SAME AS FALLING OUT OF THE WORLD, and on kilometre-scale terrain the
    // difference stopped being academic: measured on the default landform config, the surface spans
    // -141 m to 915 m, so a dive from a summit to the sea floor takes 21.7 s against a 5 s timer.
    // On the timer alone the game respawns players four times over for jumping off their own
    // mountain. So the timer only ARMS the question; what answers it is whether there is anything
    // left to land on.
    if (this.fallTicks > MAX_FALL_TICKS) {
      if (this.groundExistsBelow()) {
        this.fallTicks = 0; // still falling toward something — re-ask in another MAX_FALL_TIME
      } else {
        this.triggerRespawn();
      }
    }
  };

  /**
   * Is there anything below the player to land on, anywhere within the world's vertical reach?
   *
   * Only asked when the fall timer expires, so the cost is once per MAX_FALL_TIME of continuous
   * falling. The window is sized past the tallest configurable relief (4 km) so a genuine dive is
   * never mistaken for the void. UNKNOWN chunks read as solid here, which is the bias we want: a
   * player falling toward unstreamed terrain has NOT fallen out of the world, and the freeze
   * contract is what actually holds them until it arrives.
   */
  private groundExistsBelow(): boolean {
    if (!this.field) return true; // no field yet — never respawn on a guess
    const feetY = this.position.y - PLAYER_HEIGHT_INNER - PLAYER_RADIUS;
    this.field.columnCrossingDown(this.position.x, this.position.z, feetY, feetY - WORLD_FLOOR_REACH, _fallProbe);
    return _fallProbe.found;
  }

  /**
   * Eye position for the camera: interpolated between the last two ticks. Distinct from
   * `position` (the tick state) — using the tick state directly would judder whenever the
   * frame rate isn't a multiple of the tick rate.
   */
  getRenderEyePosition(): THREE.Vector3 {
    return this.renderEye;
  }

  /** True while physics is suspended because the chunks under/around the player are unknown. */
  isFrozen(): boolean {
    return this.stepResult.frozen;
  }

  /** Per-tick physics observables, for the debug panel / HUD. */
  getStepResult(): Readonly<PlayerStepResult> {
    return this.stepResult;
  }

  /**
   * Trigger a respawn request
   */
  private triggerRespawn(): void {
    if (this.onRespawnNeeded) {
      this.onRespawnNeeded(this.position.clone(), this.lastGroundedPosition);
    }
    this.fallTicks = 0; // prevent repeated triggers while the respawn resolves
  }

  /**
   * SPAWN the player at a position — the marker/save-restore entry point.
   *
   * Distinct from `respawn` in exactly one way, and it matters: a spawn is a FRESH START, so the
   * grounded anchor from the previous play session is dropped. A fall-respawn keeps that anchor
   * (returning you to where you were standing is the whole point of it); a spawn must not, or the
   * first fall after choosing a new spot drags you back across the map to the old one.
   *
   * Placement goes through `respawn` → `teleportPlayer(sim, …)` because THE SIM IS THE PLAYER:
   * `position` is a render mirror re-written from the sim every tick, so writing it alone (what
   * PlayerManager.setSpawnPosition used to do) was undone by the very next tick — the marker
   * spawn lasted one frame and the player snapped back to the sim's stale pose.
   */
  spawnAt(position: THREE.Vector3): void {
    this.respawn(position);
    this.lastGroundedPosition = null;
  }

  /**
   * Hard-place the player (spawn, respawn, restore). Clears velocity and the interpolation
   * history so the camera cuts rather than sweeping across the world.
   */
  respawn(position: THREE.Vector3): void {
    teleportPlayer(this.sim, position.x, position.y, position.z);
    this.position.copy(position);
    this.velocity.set(0, 0, 0);
    this.prevEye.copy(position);
    this.renderEye.copy(position);
    this.driver.reset();
    this.fallTicks = 0;
  }

  /**
   * Check if player is grounded
   */
  getIsGrounded(): boolean {
    return this.stepResult.grounded;
  }

  /**
   * Get current fall time in seconds
   */
  getFallTime(): number {
    return this.fallTicks * PHYSICS_FIXED_DT;
  }
}
